
import { defineComponent, ref, Ref } from 'vue'
import { getDeliveryDaysOnWeek, getHolidayDays, getUTCDate, stripTime } from '@/utils/deliveryDate'
import { format, getDay } from 'date-fns'

import { mapState } from 'vuex'
import type { DatePickerInstance } from '@vuepic/vue-datepicker'
/**
 * use this to get documentation of vue-datepicker
 * https://vue3datepicker.com/props/calendar-configuration/
 */
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import AsahiTypography from '@/components/library/Typography.vue'

import ChevronLeftIcon from '@/assets/icons/library/chevron-left.svg'
import ChevronRightIcon from '@/assets/icons/library/chevron-right.svg'
import CalendarIcon from '@/assets/icons/calendar-white.svg'
import AsahiButton from '../library/AsahiButton.vue'
import ExclamationIcon from '@/components/library/icons/ExclamationIcon.vue'
import AngleUpDownIcon from '@/components/library/icons/AngleUpDownIcon.vue'
import AngleLeftRightIcon from '@/components/library/icons/AngleLeftRightIcon.vue'
import { DeliveryException } from '@/store/modules/deliveryDates/interfaces/calendarException'
import formatDateMixin from '@/utils/mixin'
import { TransportationTypes } from '@/utils/transportTypes'
import { setDeliveryDate } from '@/utils/orderDetails'
import { from } from '@apollo/client'

export default defineComponent({
  name: 'DeliveryDatePickerInner',
  components: {
    AngleLeftRightIcon,
    AngleUpDownIcon,
    ExclamationIcon,
    AsahiTypography,
    VueDatePicker,
    AsahiButton
  },
  mixins: [formatDateMixin],
  props: {
    modelValue: {
      type: String,
      required: true
    },
    isCompact: {
      type: Boolean,
      default: false
    },
    shipmentId: {
      type: String,
      required: true
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isPlaceholder: {
      type: Boolean,
      default: false
    },
    hasCalendarException: {
      type: Boolean,
      default: false
    },
    rewrittenShipToId: {
      type: String,
      default: null
    },
    blackVersion: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'datePickerOpen'],

  setup () {
    const vueDatePickerRef: Ref<DatePickerInstance> = ref(null)

    return { vueDatePickerRef }
  },
  data: () => ({
    disableSelectButton: true as boolean,
    deliveryDate: '' as string,
    firstAvailableDate: '' as string,
    changedShipmentId: false,
    ChevronLeftIcon,
    ChevronRightIcon,
    CalendarIcon,
    deliveryDateToSend: '' as string,
    markers: ref([
      {
        date: new Date(),
        type: 'dot',
        color: '#1A3E13'
      }
    ]),
    inputClassName: 'input-delivery-date',
    store: '',
    isLoading: false,
    isOpen: false,
    shipToId: '',
    openedMonth: -1,
    openedYear: -1
  }),

  computed: {
    ...mapState('main', ['holidaysConfig']),
    ...mapState('catalog', ['shipTos']),
    ...mapState('deliveryDates', ['availableDays', 'unavailableDates', 'scheduleDates', 'availableExceptions', 'notificationExceptions', 'filteredDeliveryDates', 'posmInventoryDates', 'isDeliveryDateFallback']),

    user () {
      return this.$store.state.auth.user
    },

    availableDates (): string[] {
      return this.getAvailableDates()
    },

    localeValue () {
      return this.$i18n.locale as string
    },
    holidays () {
      return getHolidayDays(this.holidaysConfig)
    },
    dayOfWeek () {
      return [this.$t('MON'), this.$t('TUE'), this.$t('WED'), this.$t('THU'), this.$t('FRI'), this.$t('SAT'), this.$t('SUN')]
    },

    exceptions () {
      const today = stripTime(new Date())

      return this.availableExceptions.filter((exception: DeliveryException) => {
        const dates = [exception.originalDate, exception.scheduledDate, exception.orderBy]
          .map(d => new Date(d))
          .filter(d => !isNaN(d.getTime()))

        const isInCurrentMonth = dates.some(date =>
          date.getMonth() === this.openedMonth &&
          date.getFullYear() === this.openedYear
        )

        const allInPast = dates.every(date => stripTime(date).getTime() < today.getTime())

        return isInCurrentMonth && !allInPast
      })
    },

    getUnavailableDates () {
      if (this.unavailableDates?.length > 0) {
        return Array.from(
          this.unavailableDates.map((d: string) => {
            const dat = new Date(d)

            return format(dat, 'yyyy-MM-dd')
          }),
          (unavailableDate: string) => {
            return getUTCDate(new Date(unavailableDate))
          })
      }
      return []
    },

    getScheduleDates () {
      if (this.scheduleDates?.length > 0) {
        return Array.from(
          this.scheduleDates.map((d: string) => {
            const dat = new Date(d)

            return format(dat, 'yyyy-MM-dd')
          }),
          (scheduleDate: string) => {
            return getUTCDate(new Date(scheduleDate))
          })
      }
      return []
    }
  },

  watch: {
    modelValue: {
      handler (value) {
        if (value != null && value !== '') {
          this.deliveryDate = value
        }
      },
      immediate: true
    },

    shipmentId: {
      async handler (value, oldValue) {
        this.changedShipmentId = oldValue ? value !== oldValue : false

        if (this.changedShipmentId && this.shipToId) {
          await this.$store.dispatch('deliveryDates/GET_DELIVERY_AVAILABLE_DATE', { shipmentId: this.shipmentId, shipTo: this.shipToId, store: this.store, component: 'datePicker' })
        }
      }
    },

    isLoading () {
      this.updateInputWrapClass()
    },

    async rewrittenShipToId (newVal) {
      this.shipToId = newVal || (localStorage.getItem('shipToId') ?? '')
      if (this.shipToId) {
        await this.$store.dispatch('deliveryDates/GET_DELIVERY_AVAILABLE_DATE', { shipmentId: this.shipmentId, shipTo: this.shipToId, store: this.store })
      }
    }
  },

  async mounted () {
    this.isLoading = true
    this.store = localStorage.getItem('store') ?? ''
    if (this.isCompact) {
      this.inputClassName = 'input-delivery-date-black'
    }
    this.shipToId = this.rewrittenShipToId || (localStorage.getItem('shipToId') ?? '')
    if ((this.availableDays == null || this.availableDays?.length === 0) && this.shipToId) {
      const shipmentId = this.shipmentId !== '' ? this.shipmentId : localStorage.getItem('shippingMethod') || ''
      await this.$store.dispatch('deliveryDates/GET_DELIVERY_AVAILABLE_DATE', { shipmentId, shipTo: this.shipToId, store: this.store })
      setDeliveryDate(this.deliveryDate, shipmentId)
    }
    this.isLoading = false
  },

  methods: {
    updateInputWrapClass () {
      const inputWrap = this.vueDatePickerRef?.$el.querySelector('.dp__input_wrap')
      if (inputWrap) {
        if (this.isLoading) {
          inputWrap.classList.add('full-width')
        } else {
          setTimeout(() => {
            inputWrap.classList.remove('full-width')
          }, 2000)
        }
      }
    },
    getAvailableDates () {
      let availableDays = this.availableDays
      const datesMap = this?.posmInventoryDates
      const shipToId = this?.shipToId
      const isPOSM = this.shipmentId === TransportationTypes.POSM
      if (isPOSM && datesMap && shipToId) {
        const range = datesMap.posm
        if (Array.isArray(range) && range.length >= 2) {
          const fromTime = this.toTime(range[0])
          const toTimeVal = this.toTime(range[1])
          availableDays = availableDays.filter((availableDate:string) => {
            const date = new Date(availableDate)
            const availableDateTime = this.toTime(date)
            if (availableDateTime === null || fromTime === null || toTimeVal === null) return true
            return availableDateTime > toTimeVal || availableDateTime < fromTime
          })
        }
      }
      if (availableDays?.length > 0) {
        this.firstAvailableDate = availableDays[0]
        return Array.from(
          availableDays.map((d: string) => {
            const dat = new Date(d)

            return format(dat, 'yyyy-MM-dd')
          }),
          (availableDate: string) => {
            return getUTCDate(new Date(availableDate))
          })
      }
      return []
    },

    setFirstAvailableDate (firstAvailableDate: string) {
      const firstAvailabledDate = new Date(firstAvailableDate)
      this.onDateSelection(getUTCDate(firstAvailabledDate), true)
    },

    onDateSelection (value: string, isFirstAvailable = false) {
      const deliveryDate = new Date(value)
      this.deliveryDateToSend = getUTCDate(deliveryDate)

      if (!this.isMobile) {
        this.$emit('update:modelValue', { value: this.deliveryDateToSend, isFirstAvailable })
      }
    },

    saveChanges () {
      this.vueDatePickerRef?.selectDate()

      if (this.deliveryDateToSend !== '') {
        this.$emit('update:modelValue', this.deliveryDateToSend)
      }
      this.vueDatePickerRef?.closeMenu()

      this.onCalendarClose()
    },

    toTime (value:string | Date): number | null {
      if (!value) return null
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? null : d.getTime()
    },

    getDayClass (date: Date) {
      const weekDay = getDay(date)
      const isWeekend = (weekDay % 6) === 0
      const dateStr = format(date, 'yyyy-MM-dd')
      const todayStr = format(new Date(), 'yyyy-MM-dd')
      const deliveryDateFormatted = format(new Date(this.deliveryDate), 'yyyy-MM-dd')
      const deliveryDaysOnWeek = getDeliveryDaysOnWeek(this.shipmentId, this.shipTos)
      const firstDateFormatted = format(new Date(this.firstAvailableDate), 'yyyy-MM-dd')
      const isUnfiltered = this.shipmentId === TransportationTypes.UNFILTERED
      const isTank = this.shipmentId === TransportationTypes.TANK

      // Past dates → disabled
      const isPOSM = this.shipmentId === TransportationTypes.POSM
      if (todayStr > dateStr) {
        return 'disabled-cell'
      }

      // Holidays → holiday-cell
      if (this.holidays.includes(dateStr) || this.getUnavailableDates.includes(dateStr)) {
        return 'holiday-cell'
      }

      // Active date highlight
      if (deliveryDateFormatted === dateStr) {
        return 'active-cell'
      }

      // In fallback mode only the next working day is selectable; grey out everything else
      if (this.isDeliveryDateFallback && !this.availableDates.includes(dateStr)) {
        return 'disabled-cell'
      }

      // Unfiltered || isPOSM and Tank shipment logic
      if (isUnfiltered || isTank || isPOSM) {
        const unfilteredDateFormatted = `${dateStr}T00:00:00Z`
        const filteredDeliveryDates = (this.filteredDeliveryDates || []) as string[]
        const availableDays = (this.availableDays || []) as string[]

        if (isPOSM) {
          const posmDate = new Date(dateStr)
          posmDate.setHours(0, 0, 0, 0)
          const datesMap = this?.posmInventoryDates
          const shipToId = this?.shipToId
          if (!datesMap || !shipToId) return ''
          const range = datesMap.posm
          if (!Array.isArray(range) || range.length < 2) return ''
          const fromTime = this.toTime(range[0])
          const toTimeVal = this.toTime(range[1])
          const posmDateTime = this.toTime(posmDate)
          if (fromTime === null || toTimeVal === null || posmDateTime === null) return ''
          const start = Math.min(fromTime, toTimeVal)
          const end = Math.max(fromTime, toTimeVal)
          if (start <= posmDateTime && posmDateTime <= end) {
            return 'holiday-cell inventory-cell'
          }
          return ''
        }
        if (filteredDeliveryDates.includes(unfilteredDateFormatted)) {
          return 'delivery-cell'
        }

        if (availableDays.includes(unfilteredDateFormatted)) {
          return 'working-cell'
        }
        return 'disabled-cell'
      }

      // Normal shipment logic
      if (
        this.getScheduleDates.includes(dateStr) ||
    (this.availableDates.includes(dateStr) && deliveryDaysOnWeek.includes(weekDay))
      ) {
        return 'delivery-cell'
      }

      if (dateStr >= firstDateFormatted && !isWeekend) {
        if (isTank) {
          return 'disabled-cell'
        } else {
          return 'working-cell'
        }
      }
      return 'disabled-cell'
    },
    onCalendarOpen () {
      setTimeout(() => { this.disableSelectButton = false }, 500)
      this.shipToId = this.rewrittenShipToId || (localStorage.getItem('shipToId') ?? '')
      const component = this.$refs.vueDatePickerRef as any
      const el = component.$el as HTMLElement
      el.querySelectorAll('.dp__input_wrap').forEach((el) => {
        el.classList.add('open')
      })
      const refDate = this.deliveryDate ? new Date(this.deliveryDate) : new Date()
      this.openedMonth = refDate.getMonth()
      this.openedYear = refDate.getFullYear()
      this.$emit('datePickerOpen', true)
      this.isOpen = true
    },
    onCalendarClose () {
      this.disableSelectButton = true
      const component = this.$refs.vueDatePickerRef as any
      const el = component.$el as HTMLElement
      el.querySelectorAll('.dp__input_wrap').forEach((el) => {
        el.classList.remove('open')
      })
      this.$emit('datePickerOpen', false)
      this.isOpen = false
    },
    toggleDatePicker () {
      this.$nextTick(() => {
        if (this.vueDatePickerRef) {
          if (!this.isOpen) {
            this.vueDatePickerRef.openMenu()
          } else {
            this.vueDatePickerRef.closeMenu()
          }
        }
      })
    },

    handleMonthYear ({ month, year }: {month: number; year: number}) {
      this.openedMonth = month
      this.openedYear = year
    }
  }
})
